import { Injectable } from '@angular/core';
import { Project } from '../models/project';
import { FormulationApiService } from './echo/formulation.api.service';
import { DataSetApiService } from './echo/data-set.api.service';
import { DataSet } from '../models/echo/data-set';
import { Constants } from '../utils/constants';
import { firstValueFrom } from 'rxjs';
import { HhraGapApiService } from 'src/app/human-health-risk-assessments/hhra-gap/hhra-gap-non-dietary/hhra-gap-non-dietary.api.service';
import { EndpointDatasetService } from '../components/endpoint-dataset/endpoint-dataset.service';
import { SubstancePropertiesUSDataSet, ToxicityInfo, USHAndlerExposureApplicationScheme, UsHandlerExposureInputs } from '../models/us-handler-exposure-Inputs';
import { ModelLibraryService } from 'src/app/administrator/regulatory-models/model-library/model-library.service';
import { ModelLibrary } from '../models/model-library';
import { OPEXConstants } from '../utils/opex-constants';
import { HHRAApplicationSchemeExposure } from '../models/hhraApplicationSchemes';

@Injectable({
  providedIn: 'root'
})
export class UsHandlerExposureLogicService {
  USHandlerInputsCombinations: UsHandlerExposureInputs[]= [];
  models: ModelLibrary[] = [];
  applicationSchemes: HHRAApplicationSchemeExposure[] = [];

  constructor(
    private readonly dataSetApiService: DataSetApiService,
    private readonly HhraGapApiService: HhraGapApiService,
    private readonly formulationApiService: FormulationApiService,
    private readonly endpointDatasetService: EndpointDatasetService,
    private modelLibraryService: ModelLibraryService
  ) { }

  async fillUsHandlerExposureInputs(selectedProject: Project) {
    this.modelLibraryService.getModels().subscribe(async models => {
      this.models = [];
      this.models.push(models?.find((x: { name: string; }) => x.name === Constants.MODELS.US_HE));
    });
    const usHandlerExposureInputsList: UsHandlerExposureInputs[] = [];
    const substancePropertiesDataSetList = await this.getSubstanceProperties(selectedProject);
    const applicationSchemeNames = await this.getHumanHealthUSApplicationSchemeNames(selectedProject);

    for (let substancePropertiesDataSet of substancePropertiesDataSetList) {
      const moleculePk = substancePropertiesDataSet.moleculePk ?? substancePropertiesDataSet.metabolitePk;
      const toxicityInfoList = this.createToxicityInfo(substancePropertiesDataSet.endpoints);

      applicationSchemeNames.forEach(applicationSchemeName => {
        const humanHealthUSApplicationSchemes = this.getHumanHealthUSApplicationScheme(moleculePk, applicationSchemeName);
        if(humanHealthUSApplicationSchemes.length > 0){
          toxicityInfoList.forEach(toxicityInfo => {
            const usHandlerExposureInputs: UsHandlerExposureInputs = {
              projectFullName: selectedProject.name,
              humanHealthApplicationScheme: humanHealthUSApplicationSchemes,
              substancePropertiesDataSet: {
                substanceName: substancePropertiesDataSet.substanceName,
                description: substancePropertiesDataSet.description,
                source: substancePropertiesDataSet.source,
                projectPk: substancePropertiesDataSet.projectPk,
                dataSetPk: substancePropertiesDataSet.dataSetPk,
                toxicityInfo,
              },
            };
            usHandlerExposureInputsList.push(usHandlerExposureInputs);
          });
        }
      });
    }
    this.USHandlerInputsCombinations = usHandlerExposureInputsList;
  }

  createToxicityInfo(endpoints: any[] | undefined): ToxicityInfo[] {
    if (!endpoints) {
      return [];
    }
    const groupedEndpoints = new Map<string, any[]>();
    endpoints.forEach(endpoint => {
      if (!groupedEndpoints.has(endpoint.exposureDuration)) {
        groupedEndpoints.set(endpoint.exposureDuration, []);
      }
      groupedEndpoints.get(endpoint.exposureDuration)?.push(endpoint);
    });
    const toxicityInfoList: ToxicityInfo[] = [];
    groupedEndpoints.forEach((group, duration) => {
      let toxicityInfo: ToxicityInfo = {
        dermalNonCancerPod: 0,
        dermalNonCancerPodSourceStudy: '',
        dermalNonCancerLoc: 0,
        dermalAbsorptionFraction: 0,
        dermalAbsorptionSourceStudy: '',
        inhalationNonCancerPod: 0,
        inhalationNonCancerPodSourceStudy: '',
        inhalationNonCancerLoc: 0,
        inhalationAbsorptionFraction: 0,
        inhalationAbsorptionSourceStudy: '',
        cancerQDay: 0,
        cancerQDaySourceStudy: '',
        exposureDuration: duration,
        dermalNonCancerBodyWeight: 0,
        inhalationNonCancerBodyWeight: 0,
        cancerBodyWeight: 0,
        exposureType: ''
      };
      group.forEach(endpoint => {
        switch (endpoint.exposureType) {
          case OPEXConstants.ENDPOINT_EXPOSURE_TYPE.DERMAL:
            toxicityInfo.dermalNonCancerPod = endpoint.podNonCancer;
            toxicityInfo.dermalNonCancerPodSourceStudy = endpoint.podSourceStudy;
            toxicityInfo.dermalNonCancerLoc = endpoint.locNonCancer;
            toxicityInfo.dermalAbsorptionFraction = endpoint.fractionAbsorption;
            toxicityInfo.dermalAbsorptionSourceStudy = endpoint.absorptionSourceStudy;
            toxicityInfo.dermalNonCancerBodyWeight = endpoint.podNonCancerBodyWeight;
            break;
          case  OPEXConstants.ENDPOINT_EXPOSURE_TYPE.INHALATION:
            toxicityInfo.inhalationNonCancerPod = endpoint.podNonCancer;
            toxicityInfo.inhalationNonCancerPodSourceStudy = endpoint.podSourceStudy;
            toxicityInfo.inhalationNonCancerLoc = endpoint.locNonCancer;
            toxicityInfo.inhalationAbsorptionFraction = endpoint.fractionAbsorption;
            toxicityInfo.inhalationAbsorptionSourceStudy = endpoint.absorptionSourceStudy;
            toxicityInfo.inhalationNonCancerBodyWeight = endpoint.podNonCancerBodyWeight;
            break;
          case  OPEXConstants.ENDPOINT_EXPOSURE_TYPE.CANCER:
            toxicityInfo.cancerQDay = endpoint.qDayValue;
            toxicityInfo.cancerQDaySourceStudy = endpoint.qSourceStudy;
            toxicityInfo.cancerBodyWeight = endpoint.cancerBodyWeight;
            break;
        }
        toxicityInfo.exposureType = endpoint.exposureType;
      });

      toxicityInfoList.push(toxicityInfo);
    });
    return toxicityInfoList;
  }

  groupByApplicationScheme(
    humanHealthUSApplicationSchemes: USHAndlerExposureApplicationScheme[]
  ): Record<string, USHAndlerExposureApplicationScheme[]> {
    return humanHealthUSApplicationSchemes.reduce((grouped, scheme) => {
      if (!grouped[scheme.name]) {
        grouped[scheme.name] = [];
      }
      grouped[scheme.name].push(scheme);
      return grouped;
    }, {} as Record<string, USHAndlerExposureApplicationScheme[]>);
  }

  async getSubstanceProperties(selectedProject: Project): Promise<SubstancePropertiesUSDataSet[]> {
    const substanceProperties: SubstancePropertiesUSDataSet[] = [];

    const datasetList = await firstValueFrom(this.endpointDatasetService.getDataSetsByProject(selectedProject.projectPk, Constants.FEATURE_ACRONYM.HUMAN_HEALTH_RISK_ASSESSMENT));
    const endpointByDataSet = await firstValueFrom(this.dataSetApiService.getEndpointsByDataSetsExpoTox(datasetList));
    const formulation = await firstValueFrom(this.formulationApiService.getFormulationConcentration(selectedProject.formulationPk!));

    endpointByDataSet.forEach((dataset: DataSet) => {

      if (dataset.useInProject && dataset.endpoints?.length) {
        const substancePropertiesObject: SubstancePropertiesUSDataSet = {
          dataSetPk: dataset.dataSetPk!,
          substanceName: formulation.find(molecule => molecule.moleculePk === dataset.moleculePk)?.molecule ?? '',
          moleculePk: dataset.moleculePk!,
          metabolitePk: dataset.metabolitePk!,
          formulationPk: dataset.formulationPk!,
          description: dataset.description!,
          source: dataset.source!,
          projectPk: dataset.projectPk!,
          calculatorGroup: dataset.calculatorGroup!,
          endpoints: dataset.endpoints,
        };
        substanceProperties.push(substancePropertiesObject);
      }
    });
    return substanceProperties;
  }

  getHumanHealthUSApplicationScheme(moleculePk: number, applicationSchemeName: string): USHAndlerExposureApplicationScheme[] {
    const humanHealthUSApplicationSchemes: USHAndlerExposureApplicationScheme[] = [];
    this.applicationSchemes.filter(applicationScheme => applicationScheme.name === applicationSchemeName).forEach((applicationScheme: any) => {
      if (applicationScheme.useInProject) {
        const humanHealthUSApplicationSchemeObject: USHAndlerExposureApplicationScheme = {
          projectPk: applicationScheme.projectPk,
          name: applicationScheme.name,
          compartmentPk: applicationScheme.compartmentPk,
          rate: applicationScheme.applicationSchemeXActiveIngredientRate.find((rate: any)=> rate.moleculePk === moleculePk).rate,
          unit: applicationScheme.applicationSchemeXActiveIngredientRate.find((rate: any)=> rate.moleculePk === moleculePk).unitOfMeasure,
          workerActivity: applicationScheme.workerActivity,
          formulation: applicationScheme.formulation,
          applicationEquipment: applicationScheme.applicationEquipment,
          applicationType: applicationScheme.applicationType,
          cropTargetCategory: applicationScheme.cropTargetCategory,
          dermal: applicationScheme.dermal,
          inhalation: applicationScheme.inhalation,
        };
        humanHealthUSApplicationSchemes.push(humanHealthUSApplicationSchemeObject);
      }
    });
    return humanHealthUSApplicationSchemes ?? [];
  }

  async getHumanHealthUSApplicationSchemeNames(selectedProject: Project): Promise<string[]> {
    const compartment = await firstValueFrom(this.dataSetApiService.getCompartment(Constants.CONST_COMPARTMENT, Constants.HUMAN_HEALTH_RISK_ASSESSMENT_RUN.NON_DIETARY));
    const applicationSchemes = await firstValueFrom(this.HhraGapApiService.getHumanHealthApplicationSchemeExposureByProjectAndCompartment(
      selectedProject.projectPk,
      compartment.endpointCompartmentPk
    ));
    this.applicationSchemes = applicationSchemes;
    return Array.from(new Set(applicationSchemes.map((scheme: any) => scheme.name)));
  }

  public setUSHandlerInputsCombinations(USHandlerInputsCombinations: UsHandlerExposureInputs[]): void {
    this.USHandlerInputsCombinations = USHandlerInputsCombinations;
  }

  public getUSHandlerInputsCombinations() {
    return this.USHandlerInputsCombinations;
  }

  transformDataQC(data: UsHandlerExposureInputs[]) {
      let finalResult: any = [];
      data?.forEach(usInput => {
        usInput.humanHealthApplicationScheme.forEach(scheme => {
          let mapedInput = {
            projectFullName: usInput.projectFullName,
            applicationEquipment : scheme.applicationEquipment,
            applicationType : scheme.applicationType,
            compartmentPk: scheme.compartmentPk,
            cropTargetCategory: scheme.cropTargetCategory,
            dermal: scheme.dermal,
            formulation: scheme.formulation,
            inhalation: scheme.inhalation,
            name: scheme.name,
            projectPk: scheme.projectPk,
            rate: scheme.rate,
            unit: scheme.unit,
            workerActivity: scheme.workerActivity,
            dataSetPk: usInput.substancePropertiesDataSet.dataSetPk,
            description: usInput.substancePropertiesDataSet.description,
            source: usInput.substancePropertiesDataSet.source,
            substanceName: usInput.substancePropertiesDataSet.substanceName,
            toxicityInfo: usInput.substancePropertiesDataSet.toxicityInfo,
            models: this.models
          }
          finalResult.push(mapedInput);
        })
      });
      return finalResult;
    }
}